import { useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import queryString from 'query-string';
import { useDebounceValue } from 'usehooks-ts';
import { CustomParamsSerializer } from 'axios';

import { SearchOption, SearchOptionType } from '@app/components';
import { makeRequest } from '@app/utils';
import {
  defaultDebounceTimeMs,
  minSuggestionTermLength,
  ShapeWithTotalRecords,
} from '@app/constants';

export interface InstanceTermDto {
  term?: string;
}

export type SuggestionDtoValue = ShapeWithTotalRecords<{
  terms: InstanceTermDto[];
}>;

export type SuggestionDto = Partial<Record<SearchOption, SuggestionDtoValue>>;

export type SuggestionsDto = ShapeWithTotalRecords<{
  suggestions: SuggestionDto;
}>;

const autocompleteSuggestionsDefaultLimit = 5;
const autocompleteSuggestionsLimits = {
  [SearchOption.keyword]: 3,
};

const supportedOptions = {
  keyword: SearchOption.keyword,
  title: SearchOption.title,
  author: SearchOption.author,
  subject: SearchOption.subject,
};

export const useSuggestionsFetch = (
  searchOption: SearchOptionType
): {
  query: UseQueryResult<SuggestionsDto> | undefined;
  setSearchTerm: (value: string) => void;
} => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setTermToDebounce] = useDebounceValue(
    '',
    defaultDebounceTimeMs
  );

  useEffect(() => {
    setTermToDebounce(searchTerm);
  }, [searchTerm, setTermToDebounce]);

  const isTermLongEnough = debouncedTerm.length >= minSuggestionTermLength;
  const isSupportedOption = Boolean(supportedOptions[searchOption]);
  const isDebouncedTermUpToDate =
    debouncedTerm
      .toLocaleLowerCase()
      .startsWith(searchTerm.toLocaleLowerCase()) ||
    searchTerm
      .toLocaleLowerCase()
      .startsWith(debouncedTerm.toLocaleLowerCase());

  const suggestionsQuery = useQuery<SuggestionsDto>(
    ['suggestions', searchOption, debouncedTerm],
    () => {
      return makeRequest<SuggestionsDto>({
        url: `/search/instances/suggestions/${searchOption}`,
        params: {
          query: debouncedTerm,
          limit:
            autocompleteSuggestionsLimits[searchOption] ||
            autocompleteSuggestionsDefaultLimit,
        },
        paramsSerializer: queryString.stringify as CustomParamsSerializer,
      });
    },
    {
      enabled: Boolean(debouncedTerm) && isTermLongEnough && isSupportedOption,
      staleTime: 1000 * 60 * 5,
    }
  );

  return {
    query: isDebouncedTermUpToDate ? suggestionsQuery : undefined,
    setSearchTerm,
  };
};
